import { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { motion } from 'framer-motion';
import { useAuthStore } from '../stores/authStore';
import { useSocketStore } from '../stores/socketStore';
import { useAutoRooms } from '../hooks/useAutoRooms';
import { usePremium } from '../hooks/usePremium';
import AuthModal from '../components/AuthModal';
import LinkAccountsModal from '../components/LinkAccountsModal';
import { GAME_MODES } from '../../../shared/constants';
import {
  Music,
  Users,
  Zap,
  Trophy,
  Sparkles,
  ArrowRight,
  Play,
  Plus,
  Hash,
  Loader2,
  Crown,
  Star,
  LogIn,
  UserPlus,
  Calendar as _Calendar
} from 'lucide-react';

export default function HomePage() {
  const navigate = useNavigate();
  const { isAuthenticated } = useAuthStore();
  const { socket, isConnected, publicRooms, fetchPublicRooms, joinRoom, authAsGuest, currentRoom, resetLeftRoom } = useSocketStore();
  const { themes, isLoading: themesLoading, joinAutoRoom } = useAutoRooms();
  const { isPremium } = usePremium();
  
  // Reset le flag hasLeftRoom quand on arrive sur la page d'accueil
  useEffect(() => {
    resetLeftRoom();
  }, [resetLeftRoom]);
  
  // Naviguer vers la room quand on rejoint une auto-room
  useEffect(() => {
    if (currentRoom) {
      navigate(`/room/${currentRoom.code}`);
    }
  }, [currentRoom, navigate]);
  
  const [showJoinModal, setShowJoinModal] = useState(false);
  const [showGuestModal, setShowGuestModal] = useState(false);
  const [showAuthModal, setShowAuthModal] = useState(false);
  const [showLinkModal, setShowLinkModal] = useState(false);
  const [linkedGames, setLinkedGames] = useState<any[]>([]);
  const [linkBenefits, setLinkBenefits] = useState<string[]>([]);
  const [authModalMode, setAuthModalMode] = useState<'login' | 'register'>('login');
  const [roomCode, setRoomCode] = useState('');
  const [guestName, setGuestName] = useState('');
  const [pendingRoomCode, setPendingRoomCode] = useState<string | null>(null);
  const [pendingThemeId, setPendingThemeId] = useState<string | null>(null);
  const [currentEvent, setCurrentEvent] = useState<any>(null);

  // Fetch current weekly event
  useEffect(() => {
    if (socket && isConnected) {
      socket.emit('events:current');
      const handler = (data: any) => setCurrentEvent(data.current);
      socket.on('events:current', handler);
      return () => { socket.off('events:current', handler); };
    }
  }, [socket, isConnected]);
  
  const handleOpenLogin = () => {
    setAuthModalMode('login');
    setShowAuthModal(true);
  };
  
  const handleOpenRegister = () => {
    setAuthModalMode('register');
    setShowAuthModal(true);
  };
  
  // Vérifier si l'email a des comptes sur d'autres jeux
  const checkLinkedAccounts = async (email: string) => {
    try {
      const response = await fetch(`/auth/check-linked-accounts?email=${encodeURIComponent(email)}`);
      const data = await response.json();
      
      if (data.success && data.hasLinkedAccounts) {
        setLinkedGames(data.linkedGames);
        setLinkBenefits(data.benefits);
        setShowLinkModal(true);
      }
    } catch (error) {
      console.error('Erreur vérification comptes liés:', error);
    }
  };
  
  // Lier un compte avec un autre jeu
  const handleLinkAccount = async (gameId: string): Promise<boolean> => {
    try {
      // Récupérer le token depuis le store Zustand
      const authData = localStorage.getItem('blindtest-auth');
      let token = null;
      if (authData) {
        try {
          const parsed = JSON.parse(authData);
          token = parsed.state?.token;
        } catch (e) {
          console.error('Erreur parsing auth data:', e);
        }
      }
      
      if (!token) {
        console.error('Token non trouvé');
        return false;
      }
      
      const response = await fetch('/auth/link-account', {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${token}`
        },
        body: JSON.stringify({ targetGameId: gameId })
      });
      
      const data = await response.json();
      return data.success === true;
    } catch (error) {
      console.error('Erreur liaison compte:', error);
      return false;
    }
  };
  
  const handleGuestLogin = () => {
    if (guestName.trim().length >= 2) {
      authAsGuest(guestName.trim());
      setShowGuestModal(false);
      
      // Si on avait une room en attente, y aller
      if (pendingRoomCode) {
        setTimeout(() => {
          joinRoom(pendingRoomCode);
          navigate(`/room/${pendingRoomCode}`);
          setPendingRoomCode(null);
        }, 500); // Petit délai pour que l'auth soit traitée
      }
      
      // Si on avait un thème en attente
      if (pendingThemeId) {
        setTimeout(() => {
          handleJoinAutoRoom(pendingThemeId);
          setPendingThemeId(null);
        }, 500);
      }
    }
  };
  
  const handleJoinAutoRoom = (themeId: string) => {
    if (isAuthenticated) {
      joinAutoRoom(themeId);
      // La room:joined sera émise par le serveur, on écoute ça dans le store
      // On navigue dans un useEffect quand currentRoom change
    } else {
      setPendingThemeId(themeId);
      setShowGuestModal(true);
    }
  };
  
  const handleJoinRoom = () => {
    if (roomCode.trim().length >= 4) {
      joinRoom(roomCode.trim().toUpperCase());
      navigate(`/room/${roomCode.trim().toUpperCase()}`);
    }
  };
  
  const handleClickPublicRoom = (code: string) => {
    if (isAuthenticated) {
      joinRoom(code);
      navigate(`/room/${code}`);
    } else {
      // Demander à s'authentifier d'abord
      setPendingRoomCode(code);
      setShowGuestModal(true);
    }
  };
  
  return (
    <div className="max-w-6xl mx-auto">
      {/* Hero Section */}
      <motion.section 
        className="text-center py-12 md:py-20"
        initial={{ opacity: 0, y: 20 }}
        animate={{ opacity: 1, y: 0 }}
        transition={{ duration: 0.5 }}
      >
        <h1 className="font-display text-4xl md:text-6xl lg:text-7xl font-bold mb-6">
          <span className="gradient-text">BlindTest</span>
          <br />
          <span className="text-white">Le jeu musical</span>
        </h1>
        <p className="text-lg md:text-xl text-dark-300 mb-8 max-w-2xl mx-auto">
          Défiez vos amis, devinez les musiques, grimpez dans les classements !
          <br />
          <span className="text-primary-400">Jusqu'à 100 joueurs par partie</span>
        </p>
        
        {/* CTA Buttons */}
        <div className="flex flex-col sm:flex-row gap-4 justify-center mb-12">
          {isAuthenticated ? (
            <>
              <button 
                onClick={() => navigate('/create')}
                className="btn-primary flex items-center justify-center gap-2 text-lg"
              >
                <Plus className="w-5 h-5" />
                Créer une partie
              </button>
              <button 
                onClick={() => setShowJoinModal(true)}
                className="btn-secondary flex items-center justify-center gap-2 text-lg"
              >
                <Hash className="w-5 h-5" />
                Rejoindre
              </button>
            </>
          ) : (
            <>
              <button 
                onClick={handleOpenRegister}
                className="btn-primary flex items-center justify-center gap-2 text-lg"
              >
                <UserPlus className="w-5 h-5" />
                Créer un compte
              </button>
              <button 
                onClick={handleOpenLogin}
                className="btn-secondary flex items-center justify-center gap-2 text-lg"
              >
                <LogIn className="w-5 h-5" />
                Se connecter
              </button>
            </>
          )}
        </div>
      </motion.section>
      
      {/* Weekly Event Banner */}
      {currentEvent && (
        <motion.div
          className="bg-gradient-to-r from-purple-600/20 to-pink-600/20 border border-purple-500/30 rounded-xl p-5 mb-8"
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
        >
          <div className="flex items-center justify-between flex-wrap gap-4">
            <div className="flex items-center gap-4">
              <div className="w-14 h-14 rounded-xl bg-gradient-to-br from-purple-500 to-pink-500 flex items-center justify-center">
                <Trophy className="w-7 h-7 text-white" />
              </div>
              <div>
                <p className="font-bold text-lg text-white">{currentEvent.title}</p>
                <p className="text-sm text-dark-300">{currentEvent.description}</p>
                <p className="text-xs text-purple-400 mt-1">
                  XP x{currentEvent.xp_multiplier} &bull; Termine dans {Math.max(0, Math.ceil((new Date(currentEvent.end_date).getTime() - Date.now()) / (1000 * 60 * 60 * 24)))}j
                </p>
              </div>
            </div>
            <div className="flex gap-2">
              <button
                onClick={() => navigate('/event')}
                className="bg-dark-600 hover:bg-dark-500 text-white font-semibold px-4 py-2.5 rounded-lg transition-colors text-sm"
              >
                Classement
              </button>
              <button
                onClick={() => navigate('/create')}
                className="bg-purple-600 hover:bg-purple-500 text-white font-semibold px-5 py-2.5 rounded-lg transition-colors"
              >
                Participer
              </button>
            </div>
          </div>
        </motion.div>
      )}

      {/* Cross-promo: QuestionPourUnGeek */}
      <motion.div
        className="bg-gradient-to-r from-emerald-600/20 to-cyan-600/20 border border-emerald-500/30 rounded-xl p-4 mb-8 flex items-center justify-between gap-4 flex-wrap"
        initial={{ opacity: 0, y: 20 }}
        animate={{ opacity: 1, y: 0 }}
        transition={{ duration: 0.4, delay: 0.18 }}
      >
        <div className="flex items-center gap-3">
          <span className="text-3xl">🧠</span>
          <div>
            <p className="font-bold text-white">Tu aimes les quiz ? Essaie Question pour un Geek !</p>
            <p className="text-sm text-dark-300">Quiz de culture geek, gaming, tech et plus encore</p>
          </div>
        </div>
        <a
          href="https://questionpourungeek.com"
          target="_blank"
          rel="noopener noreferrer"
          className="bg-emerald-600 hover:bg-emerald-500 text-white font-semibold px-5 py-2.5 rounded-lg transition-colors flex items-center gap-2 whitespace-nowrap"
        >
          Jouer maintenant
        </a>
      </motion.div>

      {/* Features */}
      <motion.section
        className="grid md:grid-cols-3 gap-6 mb-16"
        initial={{ opacity: 0, y: 40 }}
        animate={{ opacity: 1, y: 0 }}
        transition={{ duration: 0.5, delay: 0.2 }}
      >
        <FeatureCard 
          icon={<Zap className="w-8 h-8" />}
          title="5 Modes de jeu"
          description="Classique, Équipes, Battle Royale, Quiz et Deaf Test"
          color="from-yellow-500 to-orange-500"
        />
        <FeatureCard 
          icon={<Users className="w-8 h-8" />}
          title="Jusqu'à 100 joueurs"
          description="Jouez avec vos amis ou rejoignez des parties publiques"
          color="from-primary-500 to-secondary-500"
        />
        <FeatureCard 
          icon={<Trophy className="w-8 h-8" />}
          title="Classements & Badges"
          description="Gagnez de l'XP, montez de niveau et débloquez des badges"
          color="from-green-500 to-emerald-500"
        />
      </motion.section>
      
      {/* Section Modes de Jeu */}
      <motion.section
        className="mb-16"
        initial={{ opacity: 0, y: 40 }}
        animate={{ opacity: 1, y: 0 }}
        transition={{ duration: 0.5, delay: 0.25 }}
      >
        <div className="flex items-center justify-between mb-6">
          <h2 className="font-display text-2xl font-bold flex items-center gap-2">
            🎮 5 Modes de Jeu
          </h2>
          <span className="text-sm text-dark-400 flex items-center gap-1">
            <Crown className="w-4 h-4 text-yellow-400" />
            4 modes Premium
          </span>
        </div>
        
        <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-5 gap-4 max-w-4xl mx-auto">
          {Object.values(GAME_MODES).map((mode, index) => (
            <motion.div
              key={mode.id}
              className={`
                relative p-4 rounded-xl border-2 transition-all
                ${mode.isPremium 
                  ? 'border-dark-600 bg-dark-700/50' 
                  : 'border-primary-500/50 bg-primary-500/10'
                }
              `}
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.3, delay: 0.1 * index }}
              whileHover={{ scale: 1.03, y: -2 }}
            >
              {mode.isPremium && (
                <div className="absolute top-2 right-2">
                  <Crown className="w-4 h-4 text-yellow-400" />
                </div>
              )}
              
              <div className={`
                w-12 h-12 rounded-xl flex items-center justify-center text-2xl mb-3
                bg-gradient-to-br ${mode.color}
              `}>
                {mode.icon}
              </div>
              
              <h3 className="font-bold text-white mb-1">{mode.name}</h3>
              <p className="text-xs text-dark-400 line-clamp-2">{mode.description}</p>
              
              {!mode.isPremium && (
                <span className="inline-block mt-2 text-xs text-primary-400 font-medium">
                  ✓ Gratuit
                </span>
              )}
            </motion.div>
          ))}
        </div>
        
        {!isPremium && (
          <motion.div 
            className="mt-4 text-center"
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            transition={{ delay: 0.5 }}
          >
            <button
              onClick={() => navigate('/premium')}
              className="inline-flex items-center gap-2 text-yellow-400 hover:text-yellow-300 transition-colors text-sm"
            >
              <Crown className="w-4 h-4" />
              Débloquez tous les modes avec Premium
              <ArrowRight className="w-4 h-4" />
            </button>
          </motion.div>
        )}
      </motion.section>
      
      {/* Auto-Rooms par thème */}
      <motion.section
        className="mb-16"
        initial={{ opacity: 0, y: 40 }}
        animate={{ opacity: 1, y: 0 }}
        transition={{ duration: 0.5, delay: 0.3 }}
      >
        <h2 className="font-display text-2xl font-bold flex items-center gap-2 mb-6">
          <Zap className="w-6 h-6 text-yellow-400" />
          Parties Rapides
        </h2>
        
        <div className="grid grid-cols-2 md:grid-cols-4 lg:grid-cols-5 gap-4">
          {themesLoading ? (
            <div className="col-span-full flex justify-center py-8">
              <Loader2 className="w-8 h-8 animate-spin text-primary-400" />
            </div>
          ) : themes.length > 0 ? (
            themes.map((theme) => (
              <motion.button
                key={theme.id}
                className="group relative overflow-hidden rounded-xl p-4 text-left transition-all duration-300 hover:scale-105"
                style={{ 
                  background: `linear-gradient(135deg, ${theme.color}33, ${theme.color}11)`,
                  border: `1px solid ${theme.color}44`
                }}
                onClick={() => handleJoinAutoRoom(theme.id)}
                whileHover={{ scale: 1.05 }}
                whileTap={{ scale: 0.98 }}
              >
                <span className="text-3xl mb-2 block">{theme.icon}</span>
                <h3 className="font-semibold text-white mb-1">{theme.name}</h3>
                <p className="text-xs text-gray-400 mb-2 line-clamp-1">{theme.description}</p>
                <div className="flex items-center gap-1 text-xs" style={{ color: theme.color }}>
                  <Users className="w-3 h-3" />
                  {theme.playerCount > 0 ? `${theme.playerCount} joueurs` : 'Rejoindre'}
                </div>
                
                {/* Glow effect */}
                <div 
                  className="absolute inset-0 opacity-0 group-hover:opacity-20 transition-opacity duration-300"
                  style={{ background: `radial-gradient(circle at center, ${theme.color}, transparent 70%)` }}
                />
              </motion.button>
            ))
          ) : (
            <div className="col-span-full text-center py-8 text-dark-400">
              <Music className="w-8 h-8 mx-auto mb-2" />
              <p>Aucun thème disponible</p>
            </div>
          )}
        </div>
      </motion.section>
      
      {/* Premium Banner - Visible pour tous les non-premium */}
      {!isPremium && (
        <motion.section
          className="mb-16"
          initial={{ opacity: 0, y: 40 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.5, delay: 0.35 }}
        >
          <div 
            className="relative overflow-hidden rounded-2xl p-6 md:p-8 cursor-pointer group"
            onClick={() => navigate('/premium')}
            style={{
              background: 'linear-gradient(135deg, rgba(234, 179, 8, 0.15) 0%, rgba(249, 115, 22, 0.15) 50%, rgba(139, 92, 246, 0.15) 100%)',
              border: '1px solid rgba(234, 179, 8, 0.3)'
            }}
          >
            {/* Effet de brillance animé */}
            <div className="absolute inset-0 opacity-0 group-hover:opacity-100 transition-opacity duration-500"
              style={{
                background: 'linear-gradient(45deg, transparent 40%, rgba(255,255,255,0.1) 50%, transparent 60%)',
                backgroundSize: '200% 200%',
                animation: 'shine 1.5s ease-in-out infinite'
              }}
            />
            
            <div className="flex flex-col md:flex-row items-center gap-6">
              {/* Icône */}
              <div className="w-20 h-20 rounded-2xl bg-gradient-to-br from-yellow-500 to-orange-500 flex items-center justify-center flex-shrink-0 group-hover:scale-110 transition-transform duration-300">
                <Crown className="w-10 h-10 text-white" />
              </div>
              
              {/* Contenu */}
              <div className="flex-1 text-center md:text-left">
                <h3 className="font-display text-2xl md:text-3xl font-bold mb-2">
                  <span className="text-yellow-400">Passez Premium</span> et débloquez tout !
                </h3>
                <p className="text-dark-300 mb-4">
                  Rooms privées, jusqu'à 100 joueurs, power-ups illimités, badges exclusifs et bien plus...
                </p>
                <div className="flex flex-wrap gap-3 justify-center md:justify-start">
                  <span className="inline-flex items-center gap-1 px-3 py-1 rounded-full bg-yellow-500/20 text-yellow-400 text-sm">
                    <Star className="w-4 h-4" /> 100 musiques max
                  </span>
                  <span className="inline-flex items-center gap-1 px-3 py-1 rounded-full bg-orange-500/20 text-orange-400 text-sm">
                    <Users className="w-4 h-4" /> 100 joueurs
                  </span>
                  <span className="inline-flex items-center gap-1 px-3 py-1 rounded-full bg-purple-500/20 text-purple-400 text-sm">
                    <Zap className="w-4 h-4" /> Power-ups ∞
                  </span>
                </div>
              </div>
              
              {/* CTA */}
              <div className="flex-shrink-0">
                <span className="btn-primary px-6 py-3 bg-gradient-to-r from-yellow-500 to-orange-500 hover:from-yellow-400 hover:to-orange-400 group-hover:scale-105 transition-transform duration-300 inline-flex items-center gap-2">
                  Découvrir
                  <ArrowRight className="w-5 h-5" />
                </span>
              </div>
            </div>
          </div>
        </motion.section>
      )}
      
      {/* Public Rooms */}
      <motion.section
        initial={{ opacity: 0, y: 40 }}
        animate={{ opacity: 1, y: 0 }}
        transition={{ duration: 0.5, delay: 0.4 }}
      >
        <div className="flex items-center justify-between mb-6">
          <h2 className="font-display text-2xl font-bold flex items-center gap-2">
            <Sparkles className="w-6 h-6 text-primary-400" />
            Parties en cours
          </h2>
          <button 
            onClick={fetchPublicRooms}
            className="text-primary-400 hover:text-primary-300 text-sm"
          >
            Actualiser
          </button>
        </div>
        
        {publicRooms.length > 0 ? (
          <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-4">
            {publicRooms.map((room) => (
              <div 
                key={room.code}
                className="card-hover cursor-pointer"
                onClick={() => handleClickPublicRoom(room.code)}
              >
                <div className="flex items-center justify-between mb-3">
                  <span className="font-mono text-primary-400 font-bold">{room.code}</span>
                  <span className="text-sm text-dark-400">{room.mode}</span>
                </div>
                <h3 className="font-semibold mb-2">{room.name}</h3>
                <div className="flex items-center justify-between text-sm text-dark-400">
                  <span>Par {room.hostName}</span>
                  <span className="flex items-center gap-1">
                    <Users className="w-4 h-4" />
                    {room.playerCount}/{room.maxPlayers}
                  </span>
                </div>
              </div>
            ))}
          </div>
        ) : (
          <div className="card text-center py-12">
            <Music className="w-12 h-12 text-dark-500 mx-auto mb-4" />
            <p className="text-dark-400">Aucune partie publique en cours</p>
            <p className="text-dark-500 text-sm mt-1">Créez la première !</p>
          </div>
        )}
      </motion.section>
      
      {/* Modal Rejoindre */}
      {showJoinModal && (
        <Modal onClose={() => setShowJoinModal(false)}>
          <h2 className="font-display text-xl font-bold mb-4">Rejoindre une partie</h2>
          <input
            type="text"
            value={roomCode}
            onChange={(e) => setRoomCode(e.target.value.toUpperCase())}
            placeholder="Code de la room (ex: ABC123)"
            className="input-field mb-4"
            maxLength={6}
            autoFocus
          />
          <button 
            onClick={handleJoinRoom}
            className="btn-primary w-full"
            disabled={roomCode.length < 4}
          >
            <ArrowRight className="w-5 h-5 inline mr-2" />
            Rejoindre
          </button>
        </Modal>
      )}
      
      {/* Modal Invité */}
      {showGuestModal && (
        <Modal onClose={() => setShowGuestModal(false)}>
          <h2 className="font-display text-xl font-bold mb-4">Jouer en invité</h2>
          <p className="text-dark-400 text-sm mb-4">
            Vous pouvez jouer sans compte, mais vous ne pourrez pas créer de partie ni sauvegarder votre progression.
          </p>
          <input
            type="text"
            value={guestName}
            onChange={(e) => setGuestName(e.target.value)}
            placeholder="Votre pseudo"
            className="input-field mb-4"
            maxLength={20}
            autoFocus
          />
          <button 
            onClick={handleGuestLogin}
            className="btn-primary w-full"
            disabled={guestName.trim().length < 2}
          >
            <Play className="w-5 h-5 inline mr-2" />
            Commencer
          </button>
        </Modal>
      )}
      
      {/* Auth Modal */}
      <AuthModal 
        isOpen={showAuthModal} 
        onClose={() => setShowAuthModal(false)}
        initialMode={authModalMode}
        onSuccess={checkLinkedAccounts}
      />
      
      {/* Link Accounts Modal */}
      <LinkAccountsModal
        isOpen={showLinkModal}
        onClose={() => setShowLinkModal(false)}
        linkedGames={linkedGames}
        benefits={linkBenefits}
        onLinkAccount={handleLinkAccount}
      />
    </div>
  );
}

// Feature Card Component
function FeatureCard({ 
  icon, 
  title, 
  description, 
  color 
}: { 
  icon: React.ReactNode; 
  title: string; 
  description: string; 
  color: string;
}) {
  return (
    <div className="card group hover:border-white/10 transition-all duration-300">
      <div className={`w-14 h-14 rounded-xl bg-gradient-to-br ${color} flex items-center justify-center mb-4 group-hover:scale-110 transition-transform`}>
        {icon}
      </div>
      <h3 className="font-semibold text-lg mb-2">{title}</h3>
      <p className="text-dark-400 text-sm">{description}</p>
    </div>
  );
}

// Modal Component
function Modal({ 
  children, 
  onClose 
}: { 
  children: React.ReactNode; 
  onClose: () => void;
}) {
  return (
    <div 
      className="fixed inset-0 bg-black/60 backdrop-blur-sm flex items-center justify-center z-50 p-4"
      onClick={onClose}
    >
      <motion.div 
        className="card max-w-md w-full"
        onClick={(e) => e.stopPropagation()}
        initial={{ opacity: 0, scale: 0.9 }}
        animate={{ opacity: 1, scale: 1 }}
        transition={{ duration: 0.2 }}
      >
        {children}
      </motion.div>
    </div>
  );
}
